// PrimaryToolbar.js
//
// Floating top-center toolbar — the "noun" selector of the noun-first UI.
// Each button chooses WHAT the user is interacting with (pan/select as the
// neutral state, a new boundary, a new line, a measurement, or the
// display-path planning panel).
//
// Design constraints:
//   - Exactly one tool is "active" at a time. Clicking the active tool
//     deactivates it (returns to the neutral pan/select state).
//   - We don't fight the existing ImageEditor mode architecture. The legacy
//     `setMode(name, true)` already zeroes every other mode in one call
//     (see ImageEditor.setMode), so we just flip one flag. The backend /
//     canvas code sees no change.
//   - "Pan" and "Select" both represent "no canvas mode active" in the
//     current codebase — pan is the default interaction (drag to move).
//     Select happens implicitly when you click a feature while no drawing
//     mode is on. We expose them as separate buttons for discoverability,
//     but both map to "clear all modes".
//   - "Display Path" is NOT a canvas mode; it opens the right-side panel
//     and triggers a computation. We surface it here because conceptually
//     it belongs with the other tool selectors, and signal the open/close
//     state via a prop from the parent (pathPanelOpen).
//   - Icons via lucide-react. A small label sits under each icon so users
//     don't have to memorize icons.
//
// TOOL_DEFS is the single source of truth: add/remove tools here.
import React from 'react';
import {
  Hand,
  MousePointer2,
  Square,
  Spline,
  Ruler,
  Route,
} from 'lucide-react';

// kind: 'neutral' clears all modes. 'mode' flips a specific mode flag in
// ImageEditor's mode map (setMode handles zeroing the others). 'panel'
// toggles an external panel (Display Path) — state owned by the parent.
// Shortcut letters mirror the handler in ImageEditor.js so users only
// have to read them in one place (tooltip + `?` help overlay).
const TOOL_DEFS = [
  { id: 'pan',      kind: 'neutral', label: 'Pan',    Icon: Hand,           shortcut: 'V', title: 'Pan the image (default) — V' },
  { id: 'select',   kind: 'mode', mode: 'selectMode', label: 'Select', Icon: MousePointer2, shortcut: 'S', title: 'Hover and click points to select them — S' },
  { id: 'boundary', kind: 'mode', mode: 'drawingBoundaryMode', label: 'Boundary',     Icon: Square, shortcut: 'B', title: 'Draw a new boundary — B' },
  { id: 'line',     kind: 'mode', mode: 'drawingMode',          label: 'Line',         Icon: Spline, shortcut: 'L', title: 'Draw a new line — L' },
  { id: 'measure',  kind: 'mode', mode: 'measureDistanceMode',  label: 'Measure',      Icon: Ruler,  shortcut: 'M', title: 'Measure distance between two points — M' },
  { id: 'path',     kind: 'panel',                              label: 'Display Path', Icon: Route,  shortcut: null, title: 'Plan and display a navigation path' },
];

// Decide which tool button should show as active. Priority: any mode-flag
// match wins; else if the display-path panel is open, 'path'; else fall
// back to the last-chosen neutral tool (pan vs select is purely visual).
const resolveActiveTool = (modes, pathPanelOpen, lastNeutral) => {
  for (const def of TOOL_DEFS) {
    if (def.kind === 'mode' && def.mode && modes && modes[def.mode]) return def.id;
  }
  if (pathPanelOpen) return 'path';
  return lastNeutral || 'pan';
};

const PrimaryToolbar = ({
  modes,
  setMode,
  pathPanelOpen = false,
  onTogglePathPanel,
  onToolChange,
}) => {
  // We track which neutral tool the user last clicked purely for highlight
  // state — the behavior is identical (all modes cleared). Stored in a ref
  // via useState so re-renders preserve it without causing extra renders.
  const [lastNeutral, setLastNeutral] = React.useState('pan');
  const activeTool = resolveActiveTool(modes, pathPanelOpen, lastNeutral);

  const clearAllModes = () => {
    if (!modes) return;
    Object.keys(modes).forEach(key => {
      if (modes[key]) setMode(key, false);
    });
  };

  const handleClick = (toolId) => {
    const def = TOOL_DEFS.find(t => t.id === toolId);
    if (!def) return;

    if (def.kind === 'neutral') {
      clearAllModes();
      setLastNeutral(def.id);
      if (onToolChange) onToolChange(def.id);
      return;
    }

    if (def.kind === 'panel') {
      // Display Path toggle. Clearing canvas modes when opening the panel
      // matches the old "Display Path" button behavior (it didn't require
      // any drawing mode to be active).
      if (!pathPanelOpen) clearAllModes();
      if (onTogglePathPanel) onTogglePathPanel(!pathPanelOpen);
      if (onToolChange) onToolChange(def.id);
      return;
    }

    // kind === 'mode': toggle the bound mode flag. setMode already zeroes
    // every other mode when flipping one on, so siblings can't collide.
    const currentlyOn = !!(modes && modes[def.mode]);
    setMode(def.mode, !currentlyOn);
    if (onToolChange) onToolChange(def.id);
  };

  return (
    <div
      role="toolbar"
      aria-label="Primary tools"
      style={{
        // Anchored bottom-center per the v2 redesign. Floats over the map
        // with a raised surface so it reads as an app chrome element, not
        // a map annotation.
        position: 'fixed',
        bottom: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'row',
        gap: '4px',
        padding: '6px',
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid #cfd7d0',
        borderRadius: '12px',
        boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
        zIndex: 1200,
      }}
    >
      {TOOL_DEFS.map(({ id, label, Icon, title }) => {
        const isActive = activeTool === id;
        return (
          <button
            key={id}
            type="button"
            title={title}
            aria-pressed={isActive}
            onClick={(e) => { e.currentTarget.blur(); handleClick(id); }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '62px',
              padding: '6px 4px',
              gap: '2px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: isActive ? '#2e7d32' : 'transparent',
              color: isActive ? 'white' : '#223',
              cursor: 'pointer',
              fontFamily: 'Arial, sans-serif',
              fontSize: '0.7rem',
              transition: 'background-color 120ms ease, color 120ms ease',
            }}
            onMouseEnter={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = '#eef4ee';
            }}
            onMouseLeave={(e) => {
              if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Icon size={20} strokeWidth={1.75} />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default PrimaryToolbar;
